import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import type { LucideIcon } from "lucide-react";
import type React from "react";
import type { JSX, ReactNode } from "react";

/**
 * The XOP command center's CRM page furniture, ported for this portal.
 *
 * Three components, each a close port of a file in `xop-edge-staff`:
 *   - `PageHeader`  ← `components/ui/page-header.tsx`
 *   - `StatsCard`   ← `crm/components/stats-card.tsx`
 *   - `CrmTable`    ← the Card > CardHeader > CardContent > Table shape
 *                     every CRM list page (Customers, Invoices, Quotes)
 *                     is built from.
 *
 * They live together in one file because they are only ever used together
 * — every CRM list page in the command center is exactly
 * `PageHeader` → a row of `StatsCard`s → one table Card — and splitting
 * three small, co-evolving pieces across three files would obscure that.
 */

/* ── PageHeader ──────────────────────────────────────────────────────── */

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { sm: "center" },
        justifyContent: { sm: "space-between" },
      }}
    >
      <Box>
        <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>
          {title}
        </Typography>
        {description && <Typography sx={{ color: "text.secondary" }}>{description}</Typography>}
      </Box>
      {actions && (
        <Box sx={{ mt: { xs: 2, sm: 0 }, display: "flex", alignItems: "center", gap: 1 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

/* ── StatsCard ───────────────────────────────────────────────────────── */

/**
 * The command center's eight gradient stripes, seeded deterministically by
 * the card title.
 *
 * Deterministic and not random for a reason worth keeping: the source used
 * `Math.random()` per render, so every card's stripe flickered through the
 * whole palette on each redraw. Hashing the title makes the colour a
 * stable property of the card.
 */
const COLOR_LINES = [
  "linear-gradient(to right, #3b82f6, #a855f7)", // blue → purple
  "linear-gradient(to right, #22c55e, #14b8a6)", // green → teal
  "linear-gradient(to right, #f97316, #ef4444)", // orange → red
  "linear-gradient(to right, #a855f7, #ec4899)", // purple → pink
  "linear-gradient(to right, #6366f1, #3b82f6)", // indigo → blue
  "linear-gradient(to right, #10b981, #06b6d4)", // emerald → cyan
  "linear-gradient(to right, #f43f5e, #f97316)", // rose → orange
  "linear-gradient(to right, #8b5cf6, #a855f7)", // violet → purple
] as const;

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function StatsCard({
  title,
  value,
  icon: Icon,
  iconColor,
  loading = false,
}: {
  title: string;
  /** Already formatted. `lib/format.ts` decides how a missing figure
   * renders (an em dash, never a zero) — this component only lays it out. */
  value: string;
  icon?: LucideIcon;
  /** Overrides the default accent. Omitted, the icon takes
   * `primary.main`, which differs per mode — a fixed `#2563eb` was under
   * AA on the dark card (spec D §7.1). */
  iconColor?: string;
  loading?: boolean;
}): JSX.Element {
  const colorLine = COLOR_LINES[hashString(title) % COLOR_LINES.length];

  return (
    <Card sx={{ position: "relative", overflow: "hidden", height: "100%" }}>
      <Box
        data-testid="stats-stripe"
        sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4 }}
        style={{ background: colorLine }}
      />
      <CardContent sx={{ px: 2, py: 1.75, height: "100%", "&:last-child": { pb: 1.75 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4, color: "text.secondary" }}>
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.375rem",
                fontWeight: 700,
                lineHeight: 1.35,
                fontVariantNumeric: "tabular-nums",
                color: loading ? "text.disabled" : "text.primary",
              }}
            >
              {loading ? "···" : value}
            </Typography>
          </Box>
          {Icon && (
            <Box sx={{ display: "flex", color: iconColor ?? "primary.main" }}>
              {/* lucide paints with `currentColor`, so the wrapper's themed
                  colour reaches the glyph without a per-mode hex here. */}
              <Icon size={26} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

/* ── CrmTable ────────────────────────────────────────────────────────── */

export interface Column<T> {
  key: string;
  header: string;
  /** Right-align numeric columns so digits line up down the column. */
  align?: "left" | "right";
  /** The crm-api column this header sorts by. Omit for a column the CRM
   * cannot order on — the header then renders plain, rather than offering a
   * control that would silently do nothing. */
  sortKey?: string;
  render: (row: T) => ReactNode;
}

export interface SortState {
  by: string | undefined;
  dir: "asc" | "desc";
}

/**
 * One table, four states — loading, error, empty, rows — resolved here
 * rather than left to each page.
 *
 * The four states are exactly where list screens usually diverge: one page
 * shows a spinner, the next shows nothing, a third renders bare headers
 * with no explanation. Centralising them means "no customers yet" and "we
 * couldn't reach the CRM" can never be mistaken for each other, which is
 * the distinction that matters most to a reseller staring at a blank
 * screen.
 *
 * `MuiTableCell` density (4px/16px padding, tamed in-cell controls) comes
 * from the shared theme, which is a verbatim copy of the command center's
 * — so these rows are the same height as the CRM's without this file
 * setting a single padding value.
 */
export function CrmTable<T>({
  columns,
  rows,
  loading,
  error,
  empty,
  rowKey,
  toolbar,
  sort,
  onSort,
  onRowClick,
}: {
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
  loading: boolean;
  error: string | null;
  /** What to say when the CRM legitimately returned nothing. */
  empty: string;
  rowKey: (row: T) => string | number;
  /** Optional filter/search row, rendered in the Card's header slot — the
   * command center puts its search field and facets exactly here. */
  toolbar?: ReactNode;
  /** Current sort. Omit `onSort` to render every header plain. */
  sort?: SortState;
  onSort?: (key: string) => void;
  /** Makes rows clickable. The cursor and hover only appear when this is
   * set, so a non-navigating table never looks interactive. */
  onRowClick?: (row: T) => void;
}): JSX.Element {
  const showRows = !loading && !error && rows.length > 0;

  return (
    <Card>
      {toolbar && <CardHeader titleTypographyProps={{ component: "div" }} title={toolbar} />}
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        {/* The scroller is on the table's own wrapper, not the page: a wide
            table must scroll inside its card rather than making the whole
            document scroll sideways. */}
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                {columns.map((col) => {
                  const sortable = Boolean(onSort && col.sortKey);
                  const active = sortable && sort?.by === col.sortKey;
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? "left"}
                      {...(active ? { "aria-sort": sort?.dir === "asc" ? "ascending" as const : "descending" as const } : {})}
                      sx={{
                        py: 1.25,
                        fontSize: 11.5,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sortable ? (
                        // MUI's TableSortLabel carries the arrow, the hover
                        // affordance and the aria-sort semantics; a plain
                        // <TableCell> is used where the CRM cannot order on
                        // the column, so a header never offers a control that
                        // would do nothing.
                        <TableSortLabel
                          active={active}
                          direction={active ? (sort?.dir ?? "asc") : "asc"}
                          onClick={() => onSort?.(col.sortKey!)}
                          sx={{ fontSize: "inherit", fontWeight: "inherit", letterSpacing: "inherit" }}
                        >
                          {col.header}
                        </TableSortLabel>
                      ) : (
                        col.header
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            {showRows && (
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={rowKey(row)}
                    hover={Boolean(onRowClick)}
                    {...(onRowClick
                      ? {
                          onClick: () => onRowClick(row),
                          sx: { cursor: "pointer" },
                          tabIndex: 0,
                          // Keyboard parity: a row that can be clicked must
                          // be reachable and activatable without a mouse.
                          onKeyDown: (e: React.KeyboardEvent) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onRowClick(row);
                            }
                          },
                        }
                      : {})}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align ?? "left"}
                        sx={{
                          py: 1.25,
                          fontSize: 13.5,
                          fontVariantNumeric: col.align === "right" ? "tabular-nums" : undefined,
                        }}
                      >
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </Box>

        {loading && (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress size={22} aria-label="Loading" />
          </Box>
        )}

        {!loading && error && (
          <Box role="alert" sx={{ px: 3, py: 5, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: "error.main" }}>
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && rows.length === 0 && (
          <Box sx={{ px: 3, py: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>{empty}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
